import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Card, Select, MenuItem, InputLabel, FormControl, Grid, Box, Accordion, AccordionSummary, AccordionDetails, CardContent, CardActionArea, IconButton } from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import { Button } from '@mui/material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';
import { db, collection, query, where, getDocs, doc, getDoc } from '../firebaseConfig';
import { Dialog } from 'primereact/dialog';
import './MonthlyCalendar.css';
import DOMPurify from 'dompurify';
import SentimentVerySatisfiedIcon from '@mui/icons-material/SentimentVerySatisfied';
import SentimentSatisfiedIcon from '@mui/icons-material/SentimentSatisfied';
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied';
import InfoIcon from '@mui/icons-material/Info';
import { calculatePipIncrement, detectAssetType } from './TradeEntryForm';
import TradeEntryForm from './TradeEntryForm';
import JournalTable from './JournalTable';
import EditSingleTrade from './EditSingleTrade';
import MDButton from './MDButton';

const MonthlyCalendar = ({ userID, tradeAccountID = 'default' }) => {
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [tooltipData, setTooltipData] = useState(null);
  const [journalEntries, setJournalEntries] = useState([]);
  const [diaryEntries, setDiaryEntries] = useState({});
  const [missedTrades, setMissedTrades] = useState([]);
  const [diaryEntry, setDiaryEntry] = useState('');
  const [displayModal, setDisplayModal] = useState(false);
  const [deposits, setDeposits] = useState([]);
  const [initialCapital, setInitialCapital] = useState(0);
  const [withdrawals, setWithdrawals] = useState([]);
  const [selectedTrades, setSelectedTrades] = useState([]);
  const [viewType, setViewType] = useState('monthly');
  const [accountName, setAccountName] = useState(tradeAccountID);
  const [previewTimeframe, setPreviewTimeframe] = useState('htf');
  const [isAddTradeOpen, setIsAddTradeOpen] = useState(false);
  const [isEditTradeOpen, setIsEditTradeOpen] = useState(false);
  const [editTradeOpen, setEditTradeOpen] = useState(false);
  const [editTradeData, setEditTradeData] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [tradesOnDate, setTradesOnDate] = useState([]);
  const [addTradeReady, setAddTradeReady] = useState(false);
  const [editTradeReady, setEditTradeReady] = useState(false);
  const allowTradeFormCloseRef = useRef(false);
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const iconColor = theme.palette.mode === 'dark' ? theme.palette.grey[200] : theme.palette.grey[600];

// ✅ Move fetchTrades to top-level so it can be reused
const fetchTrades = async () => {
  try {
    console.log('Fetching trades for tradeAccountID:', tradeAccountID);
    const tradesPath = `Users/${userID}/tradeAccounts/${tradeAccountID}/trades`;
    const q = query(collection(db, tradesPath), where('status', '==', 'Closed'));
    const journalSnapshot = await getDocs(q);

    const entries = journalSnapshot.docs.map(doc => {
      const data = doc.data();
      const risk = Math.abs(parseFloat(data.entry || 0) - parseFloat(data.sl || 0));
      const reward = Math.abs(parseFloat(data.tp || 0) - parseFloat(data.entry || 0));
      const riskRewardRatio = risk ? (reward / risk) : 0;

      const entryDateTime = data.date 
        ? (data.entryTime ? `${data.date}T${data.entryTime}` : `${data.date}`)
        : 'N/A';

      const exitDateTime = data.exitDate 
        ? (data.exitTime ? `${data.exitDate}T${data.exitTime}` : `${data.exitDate}`)
        : 'N/A';

      let actualRRRatio = 'N/A';
      if (data.status !== 'Open' && data.exit) {
        const actualReward = Math.abs(parseFloat(data.exit || 0) - parseFloat(data.entry || 0));
        const isLoss = data.shortLong === 'Long' 
          ? parseFloat(data.exit || 0) < parseFloat(data.entry || 0)
          : parseFloat(data.exit || 0) > parseFloat(data.entry || 0);
        actualRRRatio = risk 
          ? (isLoss ? -1 * (actualReward / risk).toFixed(2) : (actualReward / risk).toFixed(2))
          : 'N/A';
      }

      return {
        ...data,
        id: doc.id,
        entryDateTime,
        exitDateTime,
        symbol: data.symbol || 'N/A',
        entry: data.entry !== null && data.entry !== undefined ? parseFloat(data.entry) : 0,
        exit: data.exit !== null && data.exit !== undefined ? parseFloat(data.exit) : 0,
        sl: data.sl !== null && data.sl !== undefined ? parseFloat(data.sl) : 0,
        tp: data.tp !== null && data.tp !== undefined ? parseFloat(data.tp) : 0,
        leverage: data.leverage !== null && data.leverage !== undefined ? parseFloat(data.leverage) : 0,
        sizeType: data.sizeType || 'N/A',
        shareCount: data.shareCount !== null && data.shareCount !== undefined ? parseFloat(data.shareCount) : 0,
        lotSizeEntry: data.lotSizeEntry !== null && data.lotSizeEntry !== undefined ? parseFloat(data.lotSizeEntry) : 0,
        sizeAmount: data.sizeAmount !== null && data.sizeAmount !== undefined ? parseFloat(data.sizeAmount) : 0,
        shortLong: data.shortLong || 'N/A',
        returns: data.returns !== null && data.returns !== undefined && !isNaN(data.returns) ? parseFloat(data.returns) : 0,
        returnPercentage: data.returnPercentage !== null && data.returnPercentage !== undefined ? parseFloat(data.returnPercentage) : 0,
        riskAmount: data.riskAmount !== null && data.riskAmount !== undefined ? parseFloat(data.riskAmount) : 0,
        riskRewardRatio: parseFloat(riskRewardRatio.toFixed(2)) || 0,
        actualRRRatio,
        status: data.status || 'N/A',
        tradeConfidence: data.tradeConfidence || 'N/A',
        fxbTradeScore: data.fxbTradeScore || 'N/A',
        tags: Array.isArray(data.tags) ? data.tags : [],
        commission: data.commission !== null && data.commission !== undefined ? parseFloat(data.commission) : 0,
        comments: data.comments || 'N/A',
        screenshot: data.screenshot || null,
tradingViewUrls: {
  htf: data.tradingViewUrls?.htf || "",
  ltf: data.tradingViewUrls?.ltf || "",
  extra: data.tradingViewUrls?.extra || "",
},
      };
    });

    console.log('Fetched Trades:', entries);
    setJournalEntries(entries);
  } catch (error) {
    console.error('Error fetching journal entries:', error);
  }
};

// ✅ Fetch account name on mount or account switch
useEffect(() => {
  const fetchAccountName = async () => {
    if (tradeAccountID === 'default') {
      setAccountName('Default');
      return;
    }
    try {
      const accountDoc = await getDoc(doc(db, 'Users', userID, 'tradeAccounts', tradeAccountID));
      if (accountDoc.exists()) {
        setAccountName(accountDoc.data().name || tradeAccountID);
      }
    } catch (error) {
      console.error('Error fetching account name:', error);
    }
  };
  fetchAccountName();
}, [userID, tradeAccountID]);

// ✅ Fetch trades initially and whenever account/user changes
useEffect(() => {
  fetchTrades();
}, [userID, tradeAccountID]);


  useEffect(() => {
    const fetchDiary = async () => {
      try {
        const q = query(collection(db, 'Users', userID, 'diary'), where('userID', '==', userID));
        const diarySnapshot = await getDocs(q);
        const entries = {};
        diarySnapshot.forEach((doc) => {
          const data = doc.data();
          const formattedDate = new Date(data.date).toLocaleDateString('en-US');
          entries[formattedDate] = data.entry;
        });
        setDiaryEntries(entries);
      } catch (error) {
        console.error('Error fetching diary entries:', error);
      }
    };

    fetchDiary();
  }, [userID]);

  useEffect(() => {
    const fetchMissedTrades = async () => {
      try {
        const tradesPath = tradeAccountID === 'default' 
          ? `Users/${userID}/trades`
          : `Users/${userID}/tradeAccounts/${tradeAccountID}/trades`;
        const q = query(
          collection(db, tradesPath),
          where('status', '==', 'Missed')
        );
        const missedSnapshot = await getDocs(q);
        const entries = missedSnapshot.docs.map(doc => doc.data());
        setMissedTrades(entries);
      } catch (error) {
        console.error('Error fetching missed trades:', error);
      }
    };

    fetchMissedTrades();
  }, [userID, tradeAccountID]);

  useEffect(() => {
    const fetchCapitalChanges = async () => {
      try {
        const capitalPath = tradeAccountID === 'default' 
          ? `Users/${userID}/capital`
          : `Users/${userID}/tradeAccounts/${tradeAccountID}/capital`;
        const capitalSnapshot = await getDocs(collection(db, capitalPath));
        
        const deposits = capitalSnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(transaction => transaction.deposit);
        
        const withdrawals = capitalSnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter(transaction => transaction.withdrawal);
        
        console.log('Fetched Deposits:', deposits);
        console.log('Fetched Withdrawals:', withdrawals);
        
        setDeposits(deposits);
        setWithdrawals(withdrawals);

        const initial = deposits.reduce((sum, d) => sum + (d.deposit || 0), 0) -
                       withdrawals.reduce((sum, w) => sum + (w.withdrawal || 0), 0);
        setInitialCapital(initial > 0 ? initial : 0);
      } catch (error) {
        console.error('Error fetching capital changes:', error);
      }
    };
  
    fetchCapitalChanges();
  }, [userID, tradeAccountID]);

  const daysInMonth = new Date(selectedYear, selectedMonth, 0).getDate();
  const firstDayOfMonth = new Date(selectedYear, selectedMonth - 1, 1).getDay();

  const handleDateHover = (data) => {
    setTooltipData(data);
  };

  const calculateHoldTime = (entryDateTime, exitDate, exitTime) => {
    if (!entryDateTime || !exitDate) return 'N/A';

    const entryDate = new Date(entryDateTime);
    const exitDateTime = new Date(exitDate + 'T' + (exitTime || '00:00'));

    const diffMs = exitDateTime - entryDate;
    if (diffMs <= 0) return 'N/A';

    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    return `${diffDays} days ${diffHours} hours ${diffMinutes} minutes`;
  };

  const getConfidenceIcon = (confidence) => {
    if (!confidence) return <MDTypography variant="body3">N/A</MDTypography>;
    switch (confidence) {
      case 'High':
        return <SentimentVerySatisfiedIcon color="success" style={{ fontSize: 30 }} />;
      case 'Medium':
        return <SentimentSatisfiedIcon color="warning" style={{ fontSize: 30 }} />;
      case 'Low':
        return <SentimentDissatisfiedIcon color="error" style={{ fontSize: 30 }} />;
      default:
        return <MDTypography variant="body3">N/A</MDTypography>;
    }
  };

  const getTradeTypeLabel = (trade) => {
  const assetType = detectAssetType(trade.symbol);

  if (assetType === "equity") return "Shares";
  if (assetType === "futures") return "Contracts";

  if (trade.sizeType === "lot") return "Lots";
  if (trade.sizeType === "dollar") return "$";

  return "N/A";
};

const getPositionSizeDisplay = (trade) => {
  const assetType = detectAssetType(trade.symbol);

  if (assetType === "equity") {
    const shares = Number(trade.shareCount || 0);
    return `${shares} ${shares === 1 ? "share" : "shares"}`;
  }

  if (assetType === "futures") {
    const contracts = Number(trade.lotSizeEntry || 0);
    return `${contracts} ${contracts === 1 ? "contract" : "contracts"}`;
  }

  if (trade.sizeType === "lot") {
    const lots = Number(trade.lotSizeEntry || 0);
    const formattedLots =
      lots % 1 === 0 ? lots.toString() : lots.toString().replace(/\.?0+$/, "");
    return `${formattedLots} ${lots === 1 ? "lot" : "lots"}`;
  }

  if (trade.sizeType === "dollar") {
    const dollars = Number(trade.sizeAmount || 0);
    return `$${dollars.toLocaleString()}`;
  }

  return "N/A";
};

const handleEditTrade = (trade) => {
  setEditTradeData(trade);
  setEditTradeOpen(true);
};
  const openModal = (entry, trades) => {
    setDiaryEntry(entry);
    setSelectedTrades(trades);
    setPreviewTimeframe('htf'); // 👈 reset each time
    setDisplayModal(true);
  };

  const closeModal = () => {
    setDisplayModal(false);
  };

  const renderTradeScore = (fxbTradeScore) => {
    if (fxbTradeScore === 'N/A' || !fxbTradeScore) {
      return <MDTypography variant="body2">N/A</MDTypography>;
    }
  
    const [score, rest] = fxbTradeScore.split('/');
    const scoreNum = parseInt(score, 10);
  
    let color;
    if (scoreNum >= 80) color = 'success';
    else if (scoreNum >= 50) color = 'warning';
    else color = 'error';
  
    return (
      <MDTypography variant="body2">
        <span style={{ color: theme.palette[color].main }}>{score}</span>
        <span>/{rest}</span>
      </MDTypography>
    );
  };

const handleDateClick = async (date) => {
  const rawDiaryEntry = diaryEntries[date] || '';
  let sanitizedEntry = DOMPurify.sanitize(rawDiaryEntry, { ALLOWED_TAGS: [] });
  sanitizedEntry = sanitizedEntry.replace(/<\/?p>/gi, '');

  const tradesOnDate = journalEntries.filter(
    (entry) => new Date(entry.date).toLocaleDateString('en-US') === date
  );

  const isoDate = new Date(date).toISOString().split('T')[0]; // ✅ convert to yyyy-MM-dd

  setDiaryEntry({
    entry: sanitizedEntry,
    trades: tradesOnDate,
  });

  setTradesOnDate(tradesOnDate);
  setSelectedDate(isoDate); // ✅ use ISO format
  setDisplayModal(true);
};


  const handleDateLeave = () => {
    setTooltipData(null);
  };

  const hideDialog = () => {
    setDisplayModal(false);
  };

  const handlePrevMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

const calculateMonthlyStats = () => {
    const monthStart = new Date(selectedYear, selectedMonth - 1, 1);

    // Capital at the very start of the selected month
    const previousPnL = journalEntries
      .filter(t => new Date(t.date) < monthStart)
      .reduce((sum, t) => sum + (t.returns || 0), 0);

    const depositsBefore = deposits
      .filter(d => new Date(d.date) < monthStart)
      .reduce((sum, d) => sum + (d.deposit || 0), 0);

    const withdrawalsBefore = withdrawals
      .filter(w => new Date(w.date) < monthStart)
      .reduce((sum, w) => sum + (w.withdrawal || 0), 0);

    const startingCapital = initialCapital + previousPnL + depositsBefore - withdrawalsBefore;

    // Trades in the selected month
    const monthTrades = journalEntries.filter(t => {
      const d = new Date(t.date);
      return d.getFullYear() === selectedYear && d.getMonth() === selectedMonth - 1;
    });

    const totalPnL = monthTrades.reduce((sum, t) => sum + (t.returns || 0), 0);
    const tradeCount = monthTrades.length;

    const monthlyReturn = startingCapital > 0
      ? ((totalPnL / startingCapital) * 100).toFixed(2)
      : "0.00";

    return { totalPnL, monthlyReturn, tradeCount, startingCapital };
  };

const calculateYearlyStats = () => {
  // Force UTC to avoid any local timezone shifting dates
  const getUTCDate = (year, month, day = 1) => {
    return new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
  };

  const yearStartUTC = getUTCDate(selectedYear, 0, 1); // Jan 1, 00:00:00 UTC

  // Capital at the beginning of the selected year (UTC-safe)
  const previousPnL = journalEntries
    .filter(t => new Date(t.date) < yearStartUTC)
    .reduce((sum, t) => sum + (t.returns || 0), 0);

  const depositsBeforeYear = deposits
    .filter(d => new Date(d.date) < yearStartUTC)
    .reduce((sum, d) => sum + (d.deposit || 0), 0);

  const withdrawalsBeforeYear = withdrawals
    .filter(w => new Date(w.date) < yearStartUTC)
    .reduce((sum, w) => sum + (w.withdrawal || 0), 0);

  const yearStartingCapital = initialCapital + previousPnL + depositsBeforeYear - withdrawalsBeforeYear;

  let ytdPnL = 0;

  return Array.from({ length: 12 }, (_, monthIndex) => {
    // Month boundaries in UTC — this is the key fix
    const monthStartUTC = getUTCDate(selectedYear, monthIndex, 1);
    const monthEndUTC   = getUTCDate(selectedYear, monthIndex + 1, 0); // last day of month
    monthEndUTC.setUTCHours(23, 59, 59, 999); // include full last day

    const monthTrades = journalEntries.filter(t => {
      const tradeDate = new Date(t.date);
      return tradeDate >= monthStartUTC && tradeDate <= monthEndUTC;
    });

    const monthlyPnL = monthTrades.reduce((sum, t) => sum + (t.returns || 0), 0);
    ytdPnL += monthlyPnL;

    // Capital at the start of this month (also UTC-safe)
    const prevMonthsPnL = journalEntries
      .filter(t => {
        const d = new Date(t.date);
        return d < monthStartUTC && d.getUTCFullYear() === selectedYear;
      })
      .reduce((sum, t) => sum + (t.returns || 0), 0);

    const depositsThisYearBeforeMonth = deposits
      .filter(d => {
        const dDate = new Date(d.date);
        return dDate < monthStartUTC && dDate.getUTCFullYear() === selectedYear;
      })
      .reduce((sum, d) => sum + (d.deposit || 0), 0);

    const withdrawalsThisYearBeforeMonth = withdrawals
      .filter(w => {
        const wDate = new Date(w.date);
        return wDate < monthStartUTC && wDate.getUTCFullYear() === selectedYear;
      })
      .reduce((sum, w) => sum + (w.withdrawal || 0), 0);

    const monthStartingCapital = yearStartingCapital +
      prevMonthsPnL +
      depositsThisYearBeforeMonth -
      withdrawalsThisYearBeforeMonth;

    const monthlyReturn = monthStartingCapital > 0
      ? ((monthlyPnL / monthStartingCapital) * 100).toFixed(2)
      : "0.00";

    const ytdReturn = yearStartingCapital > 0
      ? ((ytdPnL / yearStartingCapital) * 100).toFixed(2)
      : "0.00";

    return {
      month: monthIndex,
      totalPnL: monthlyPnL,
      tradeCount: monthTrades.length,
      monthlyReturn,
      ytdReturn
    };
  });
};

  const generateYearlyView = () => {
    const yearStats = calculateYearlyStats();
    
    return (
      <MDBox sx={{ height: '590px', display: 'flex', flexDirection: 'column', overflow: 'auto', borderRadius: '12px' }}>
      <Grid 
        container 
        spacing={1} 
        sx={{ 
          p: 1,
          backgroundColor: theme.palette.background.default,
        }}
      >
          {yearStats.map((monthStat, index) => {
            const monthName = new Date(selectedYear, index).toLocaleString('default', { month: 'long' });
            let backgroundColor;
            let borderColor;
            let hoverColor;
            
            if (monthStat.totalPnL > 0) {
              backgroundColor = isDarkMode ? '#2e7d32' : '#5FB663';
              borderColor = isDarkMode ? '#1b5e20' : '#4CAF50';
              hoverColor = isDarkMode ? '#388e3c' : '#81c784';
            } else if (monthStat.totalPnL < 0) {
              backgroundColor = isDarkMode ? '#d32f2f' : '#EA4643';
              borderColor = isDarkMode ? '#b71c1c' : '#D32F2F';
              hoverColor = isDarkMode ? '#e53935' : '#ef5350';
            } else if (monthStat.tradeCount > 0) {
              backgroundColor = isDarkMode ? '#f57c00' : '#FFA726';
              borderColor = isDarkMode ? '#e65100' : '#F57C00';
              hoverColor = isDarkMode ? '#fb8c00' : '#ffb300';
            } else {
              backgroundColor = theme.palette.background.default;
              borderColor = theme.palette.divider;
              hoverColor = isDarkMode ? theme.palette.grey[200] : theme.palette.grey[500];
            }

          return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
              <Card
                sx={{
                  backgroundColor,
                  border: `2px solid ${borderColor}`,
                  borderRadius: '12px',
                  overflow: 'hidden',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: theme.shadows[8],
                    backgroundColor: hoverColor,
                  },
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <CardActionArea
                  onClick={() => {
                    setSelectedMonth(index + 1);
                    setViewType('monthly');
                  }}
                  sx={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      boxShadow: theme.shadows[8],
                      backgroundColor: hoverColor,
                    },
                    p: 1,
                  }}
                >
                  <CardContent sx={{ p: 1.5, flex: 1 }}>
                    <MDTypography 
                      variant="h5" 
                      fontWeight="medium" 
                      mb={2}
                    >
                      {monthName}
                    </MDTypography>
                    <MDBox display="flex" flexDirection="column" gap={1}>
                      <MDTypography 
                        variant="body3" 
                        color={isDarkMode ? 'grey.300' : 'text.secondary'}
                      >
                        Trades: {monthStat.tradeCount}
                      </MDTypography>
                      <MDTypography 
                        variant="body3" 
                        color={monthStat.totalPnL >= 0 ? 'success.main' : 'error.main'}
                        fontWeight="medium"
                      >
                        PnL: ${monthStat.totalPnL.toFixed(2)}
                      </MDTypography>
                      <MDTypography 
                        variant="body3" 
                        color={isDarkMode ? 'grey.300' : 'text.secondary'}
                      >
                        Return: {monthStat.monthlyReturn}%
                      </MDTypography>
                      <MDTypography 
                        variant="body3" 
                        color={isDarkMode ? 'grey.300' : 'text.secondary'}
                      >
                        YTD Return: {monthStat.ytdReturn}%
                      </MDTypography>
                    </MDBox>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>
      </MDBox>
    );
  };

const getMoveLabel = (trade) => {
  const assetType = detectAssetType(trade.symbol);
  if (assetType === "futures") return "Ticks";
  if (assetType === "equity") return "Price Move";
  return "Pips";
};

const getMoveValue = (trade) => {
  const entry = parseFloat(trade.entry);
  const exit = parseFloat(trade.exit);
  if (isNaN(entry) || isNaN(exit)) return "N/A";

  const assetType = detectAssetType(trade.symbol);

  // Futures: treat your increment as tick size => ticks count
  if (assetType === "futures") {
    const inc = calculatePipIncrement(trade.symbol);
    if (!inc || isNaN(inc) || inc === 0) return "N/A";
    return Math.abs((entry - exit) / inc).toFixed(0);
  }

  // Equity: raw price move
  if (assetType === "equity") {
    const move = Math.abs(exit - entry);
    return move % 1 === 0 ? move.toString() : move.toString().replace(/\.?0+$/, "");
  }

  // Forex: pips
  const inc = calculatePipIncrement(trade.symbol);
  if (!inc || isNaN(inc) || inc === 0) return "N/A";
  return Math.abs((entry - exit) / inc).toFixed(0);
};
  const calculateWeeklyStats = (startDate, endDate) => {
    const tradesInWeek = journalEntries.filter(trade => {
      const tradeDate = new Date(trade.date);
      return tradeDate >= startDate && tradeDate <= endDate;
    });

    const tradingDays = [...new Set(tradesInWeek.map(trade => 
      new Date(trade.date).toLocaleDateString('en-US')
    ))].length;

    const weeklyPnL = tradesInWeek.reduce((sum, trade) => sum + (trade.returns || 0), 0);

    return {
      tradingDays,
      weeklyPnL
    };
  };

  const stats = calculateMonthlyStats();

  const generateCalendar = () => {
    const dates = [];
    const totalSlots = firstDayOfMonth + daysInMonth;
    const rows = [];
    let cells = [];
    let weekStartDate = new Date(selectedYear, selectedMonth - 1, 1);
  
    for (let i = 0; i < totalSlots; i++) {
      const date = i - firstDayOfMonth + 1;
      const isCurrentMonth = date > 0 && date <= daysInMonth;
      const currentDate = new Date(selectedYear, selectedMonth - 1, date);
      const formattedDate = currentDate.toLocaleDateString('en-US');

      // Track week start date
      if (i % 7 === 0 && isCurrentMonth) {
        weekStartDate = new Date(currentDate);
      }
  
      const tradesOnDate = journalEntries.filter(
        (entry) =>
          new Date(entry.date).getFullYear() === currentDate.getFullYear() &&
          new Date(entry.date).getMonth() === currentDate.getMonth() &&
          new Date(entry.date).getDate() === currentDate.getDate()
      );
  
      const missedTradesOnDate = missedTrades.filter(
        (entry) =>
          new Date(entry.date).getFullYear() === currentDate.getFullYear() &&
          new Date(entry.date).getMonth() === currentDate.getMonth() &&
          new Date(entry.date).getDate() === currentDate.getDate()
      );
  
      const totalReturns = tradesOnDate.reduce((sum, entry) => sum + (entry.returns || 0), 0);
      const missedTradeOnDate = missedTradesOnDate[0];
  
      let dateClass = '';
      if (totalReturns > 0) {
        dateClass = 'green';
      } else if (totalReturns < 0) {
        dateClass = 'red';
      } else if (totalReturns === 0 && tradesOnDate.length > 0) {
        dateClass = 'orange';
      } else if (missedTradeOnDate) {
        dateClass = 'purple';
      } else if (diaryEntries[formattedDate] && !tradesOnDate.length) {
        dateClass = 'yellow';
      }
  
      const hoverData = { returns: totalReturns, tradesCount: tradesOnDate.length, missedTrade: missedTradeOnDate, date };
  
      cells.push(
        <Grid item xs={1.5} key={i}>
          <MDBox
            className={`date ${isCurrentMonth ? 'current-month' : 'other-month'} ${dateClass}`}
            onClick={() => isCurrentMonth && handleDateClick(formattedDate)}
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="67px"
            width="auto"
            borderRadius="md"
            boxShadow={isCurrentMonth ? "sm" : undefined}
            sx={{
              cursor: isCurrentMonth ? 'pointer' : 'default',
            }}
          >
            {isCurrentMonth ? (
              <MDBox className="date-content">
                <MDTypography variant="caption">
                  {date}
                </MDTypography>
                {hoverData.tradesCount > 0 && (
                  <MDBox className="trade-tooltip">
                    {hoverData.returns !== undefined && (
                      <>
                        <MDTypography variant="caption" className={`return ${hoverData.missedTrade ? 'missed' : (hoverData.returns >= 0 ? 'positive' : 'negative')}`}>
                          {hoverData.missedTrade ? `$${Math.abs(hoverData.missedTrade.returns).toFixed(2)}` : (hoverData.returns >= 0 ? '+' : '-') + `$${Math.abs(hoverData.returns).toFixed(2)} `}
                        </MDTypography>
                        <span className="tooltip-separator">|</span>
                      </>
                    )}
                    <MDTypography variant="caption">
                      {`${hoverData.tradesCount} ${hoverData.tradesCount === 1 ? 'trade' : 'trades'}`}
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>
            ) : (
              ''
            )}
          </MDBox>
        </Grid>
      );
  
      // Add weekly summary column after every 7 days or at the end of the month
      if (((i + 1) % 7 === 0 || (isCurrentMonth && date === daysInMonth)) && isCurrentMonth) {
        const weekEndDate = new Date(currentDate);
        const weekStats = calculateWeeklyStats(weekStartDate, weekEndDate);
        
        cells.push(
          <Grid item xs={1.5} key={`week-summary-${i}`}>
            <MDBox
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              height="67px"
              width="auto"
              borderRadius="md"
              boxShadow="sm"
              sx={{
                backgroundColor: theme.palette.background.default,
                border: `1px solid ${theme.palette.divider}`,
              }}
            >
              <MDTypography variant="caption" fontWeight="medium">
                Week
              </MDTypography>
              <MDTypography variant="caption">
                Days: {weekStats.tradingDays}
              </MDTypography>
              <MDTypography 
                variant="caption" 
                color={weekStats.weeklyPnL >= 0 ? "success" : "error"}
              >
                PnL: ${weekStats.weeklyPnL.toFixed(2)}
              </MDTypography>
            </MDBox>
          </Grid>
        );
        rows.push(<Grid container item xs={12} spacing={1} key={i / 7}>{cells}</Grid>);
        cells = [];
      }
    }
  
    return rows;
  };
  
  const tooltipContent = `Monthly Calendar View:

The Monthly Calendar View showcases your daily trading activity for the selected account, summarizing trades and returns, with clickable dates for detailed insights and diary entries. Weekly summaries show trading days and PnL.`;

  return (
    <Card>
      <MDBox display="flex" flexDirection="column" justifyContent="space-between" pt={1} px={2} height="100%">
        <MDBox display="flex" justifyContent="space-between">
          <MDBox
            variant="gradient"
            bgColor="light"
            color="dark"
            coloredShadow="light"
            borderRadius="xl"
            display="flex"
            justifyContent="center"
            alignItems="center"
            width="4rem"
            height="4rem"
            mt={-3}
          >
            <CalendarTodayIcon fontSize="medium" color="inherit" />
          </MDBox>
          <MDBox textAlign="right" lineHeight={1.25}>
            <MDBox display="flex" alignItems="center">
              <MDTypography variant="button" fontWeight="regular" color="text" fontFamily="inherit">
                {viewType === 'monthly' ? 'Monthly Calendar' : 'Yearly Overview'} {tradeAccountID === 'default' ? '' : `(${accountName})`}
              </MDTypography>
              <Button 
                variant="outlined"
                size="small"
                onClick={() => setViewType(viewType === 'monthly' ? 'yearly' : 'monthly')}
                sx={{ 
                  ml: 2,
                  color: iconColor,
                }}
              >
                <MDTypography variant="button">
                  Switch to {viewType === 'monthly' ? 'Yearly' : 'Monthly'} View
                </MDTypography>
              </Button>
              <Tooltip 
                title={tooltipContent}
                placement="bottom-end"
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: theme.palette.background.paper,
                      color: theme.palette.text.primary,
                      border: `1px solid ${theme.palette.divider}`,
                      p: 1.5,
                      maxWidth: '250px',
                      fontSize: '0.75rem',
                      lineHeight: 1.5,
                      '& .MuiTooltip-arrow': {
                        color: theme.palette.background.paper,
                      }
                    }
                  }
                }}
              >
                <InfoIcon 
                  sx={{ 
                    ml: 1, 
                    fontSize: '1rem',
                    color: iconColor,
                    cursor: 'help'
                  }} 
                />
              </Tooltip>
            </MDBox>
          </MDBox>
        </MDBox>

        <MDBox>
          <Grid container spacing={2} alignItems="center">
            {viewType === 'monthly' && (
              <Grid item xs={6}>
                <MDBox display="flex" alignItems="center">
                  <IconButton onClick={handlePrevMonth} sx={{ mr: 1 }}>
                    <ArrowBackIosIcon sx={{ color: iconColor }} />
                  </IconButton>
                  <FormControl variant="outlined" fullWidth margin="normal">
                    <InputLabel id="month-label">Month</InputLabel>
                    <Select
                      labelId="month-label"
                      id="month"
                      value={selectedMonth}
                      onChange={(e) => setSelectedMonth(parseInt(e.target.value, 10))}
                      label="Month"
                    >
                      {Array.from({ length: 12 }, (_, index) => (
                        <MenuItem key={index + 1} value={index + 1}>
                          {new Date(selectedYear, index, 1).toLocaleDateString('en-US', { month: 'long' })}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <IconButton onClick={handleNextMonth} sx={{ ml: 1 }}>
                    <ArrowForwardIosIcon sx={{ color: iconColor }} />
                  </IconButton>
                </MDBox>
              </Grid>
            )}
            <Grid item xs={viewType === 'monthly' ? 6 : 12}>
              <FormControl variant="outlined" fullWidth margin="normal">
                <InputLabel id="year-label">Year</InputLabel>
                <Select
                  labelId="year-label"
                  id="year"
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
                  label="Year"
                >
                  {Array.from({ length: 5 }, (_, index) => (
                    <MenuItem key={index + (new Date().getFullYear() - 2)} value={index + (new Date().getFullYear() - 2)}>
                      {index + (new Date().getFullYear() - 2)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </MDBox>

        {viewType === 'monthly' ? (
          <MDBox sx={{ height: '590px', display: 'flex', flexDirection: 'column' }}>
            <MDBox className="calendar" sx={{ flex: 1 }}>
              <Grid container spacing={1}>
                <Grid container item xs={12} className="weekdays">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Week'].map((day, index) => (
                    <Grid item xs={1.5} key={index}>
                      <MDTypography variant="caption" fontWeight="bold" align="center">
                        {day}
                      </MDTypography>
                    </Grid>
                  ))}
                </Grid>
                {generateCalendar()}
                </Grid>
              </MDBox>

            <MDBox 
              display="flex" 
              justifyContent="space-between" 
              alignItems="center" 
              mt={2} 
              px={2}
            >
              <MDTypography variant="caption" fontWeight="medium">
                Total Trades: {stats.tradeCount}
              </MDTypography>
              <MDTypography 
                variant="caption" 
                fontWeight="medium" 
                color={stats.totalPnL >= 0 ? "success" : "error"}
              >
                PnL: ${stats.totalPnL.toFixed(2)}
              </MDTypography>
              <MDTypography 
                variant="caption" 
                fontWeight="medium" 
                color={stats.totalPnL >= 0 ? "success" : "error"}
              >
                Monthly Return: {stats.monthlyReturn}%
              </MDTypography>
            </MDBox>

            <MDBox display="flex" alignItems="center" my={2}>
              <Box mr={1} width={20} height={20} bgcolor="#5FB663" borderRadius="md"></Box>
              <MDTypography variant="body2">Winning Trade Day</MDTypography>
              <Box mx={1} className="tooltip-separator">|</Box>
              <Box mr={1} width={20} height={20} bgcolor="#EA4643" borderRadius="md"></Box>
              <MDTypography variant="body2">Losing Trade Day</MDTypography>
              <Box mx={1} className="tooltip-separator">|</Box>
              <Box mr={1} width={20} height={20} bgcolor="#FFA726" borderRadius="md"></Box>
              <MDTypography variant="body2">Break Even Trade Day</MDTypography>
              <Box mx={1} className="tooltip-separator">|</Box>
              <Box mr={1} width={20} height={20} bgcolor="#8A2BE2" borderRadius="md"></Box>
              <MDTypography variant="body2">Missed Trade Day</MDTypography>
              <Box mx={1} className="tooltip-separator">|</Box>
              <Box mr={1} width={20} height={20} bgcolor="#4F8BC7" borderRadius="md"></Box>
              <MDTypography variant="body2">No Trades, Diary Entry</MDTypography>
            </MDBox>
          </MDBox>
        ) : (
          generateYearlyView()
        )}
      </MDBox>
      
      <Dialog
        visible={displayModal}
        onHide={hideDialog}
        header={
          <MDTypography variant="h3">
            Diary Entry & Trades
          </MDTypography>
        }
        modal
        className="modal-calendar-style"
        style={{ padding: 16, backgroundColor: theme.palette.background.default || '#bac7db' }}
      >
        <MDBox display="flex" gap={2} mb={2}>
<MDButton
  variant="contained"
  color="success"
  onClick={() => {
    allowTradeFormCloseRef.current = false;
    setIsAddTradeOpen(true);
    setAddTradeReady(true);
  }}
>
  ➕ Add Trade
</MDButton>


</MDBox>
        <Accordion
          sx={{
            backgroundColor: theme.palette.background.default,
            color: theme.palette.text.primary,
            boxShadow: theme.shadows[1],
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: isDarkMode ? theme.palette.text.primary : theme.palette.text.secondary }} />} sx={{ backgroundColor: theme.palette.background.default, color: theme.palette.text.primary, boxShadow: theme.shadows[1] }}>
            <MDTypography variant="h6">{diaryEntry ? 'Diary Entry' : 'No Diary Entry'}</MDTypography>
          </AccordionSummary>
          <AccordionDetails>
            {diaryEntry ? (
              <MDTypography variant="body1">
                {diaryEntry.entry}
              </MDTypography>
            ) : (
              <MDTypography variant="body2" color="textSecondary">
                No diary entry for this date.
              </MDTypography>
            )}
          </AccordionDetails>
        </Accordion>

        {diaryEntry.trades && diaryEntry.trades.length > 0 ? (
          diaryEntry.trades.map((trade, index) => (
            <Accordion key={index}
              sx={{
                backgroundColor: theme.palette.background.default,
                color: theme.palette.text.primary,
                boxShadow: theme.shadows[1],
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: isDarkMode ? theme.palette.text.primary : theme.palette.text.secondary }} />} sx={{ backgroundColor: theme.palette.background.default, color: theme.palette.text.primary, boxShadow: theme.shadows[1] }}>
                <MDTypography variant="h6">Trade {index + 1}</MDTypography>
              </AccordionSummary>
              <AccordionDetails>
                <MDBox display="flex" flexDirection="row">
                  <MDBox flex={1}>
                    <MDTypography variant="body2">Entry Date and Time: {trade.entryDateTime !== 'N/A' ? new Date(trade.entryDateTime).toLocaleString() : 'N/A'}</MDTypography>
                    <MDTypography variant="body2">Exit Date and Time: {new Date(trade.exitDate + 'T' + (trade.exitTime || '00:00')).toLocaleString()}</MDTypography>
                    <MDTypography variant="body2">Hold Time: {calculateHoldTime(trade.entryDateTime, trade.exitDate, trade.exitTime)}</MDTypography>
                    <MDTypography variant="body2">Symbol: {trade.symbol}</MDTypography>
                    <MDTypography variant="body2">Entry: {trade.entry}</MDTypography>
                    <MDTypography variant="body2">Exit: {trade.exit}</MDTypography>
                    <MDTypography variant="body2">Leverage: {trade.leverage || 'N/A'}</MDTypography>
                    <MDBox display="flex" flexDirection="row">
                      <MDTypography variant="body2"> Type: {getTradeTypeLabel(trade)}</MDTypography>

                    </MDBox>
                    <MDTypography variant="body2">Position Size: {getPositionSizeDisplay(trade)}</MDTypography>                    
                    <MDTypography variant="body2">Returns $: {trade.returns.toFixed(2)}</MDTypography>
                    {detectAssetType(trade.symbol) !== "futures" && (
  <MDTypography variant="body2">
    Return %: {Number(trade.returnPercentage || 0).toFixed(2)}
  </MDTypography>
)}
                    <MDTypography variant="body2">SL: {trade.sl}</MDTypography>
                    <MDTypography variant="body2">TP: {trade.tp}</MDTypography>
                    <MDTypography variant="body2">
  {getMoveLabel(trade)}: {getMoveValue(trade)}
</MDTypography>
                    <MDTypography variant="body2">Risk Amount $: {trade.riskAmount.toFixed(2)}</MDTypography>
                    <MDTypography variant="body2">Targeted RR: {trade.riskRewardRatio}</MDTypography>
                    <MDTypography variant="body2">Actual RR: {trade.actualRRRatio}</MDTypography>
                    <MDTypography variant="body2">Comments: {trade.comments}</MDTypography>
                    <MDTypography variant="body2">Status: {trade.status}</MDTypography>
                    <MDTypography variant="body2">Trade Confidence: {getConfidenceIcon(trade.tradeConfidence)}</MDTypography>
                    <MDTypography variant="body2" display="flex" alignItems="center">
                      Trade Score:{' '}
                      <span style={{ marginLeft: '4px' }}>{renderTradeScore(trade.fxbTradeScore)}</span>
                    </MDTypography>
                    <MDTypography variant="body2">Tags: {Array.isArray(trade.tags) && trade.tags.length > 0 ? trade.tags.join(', ') : 'N/A'}</MDTypography>
                    <MDTypography variant="body2">Commission Fee: {trade.commission}</MDTypography>
                  </MDBox>
                  <MDBox flex={1.5}>
<MDBox mb={2}>
  <MDBox display="flex" gap={1} mb={1}>
    <Button
      size="small"
      variant={previewTimeframe === 'htf' ? 'contained' : 'outlined'}
      disabled={!trade.tradingViewUrls?.htf}
      onClick={() => setPreviewTimeframe('htf')}
    >
      HTF
    </Button>
    <Button
      size="small"
      variant={previewTimeframe === 'ltf' ? 'contained' : 'outlined'}
      disabled={!trade.tradingViewUrls?.ltf}
      onClick={() => setPreviewTimeframe('ltf')}
    >
      LTF
    </Button>
    <Button
      size="small"
      variant={previewTimeframe === 'extra' ? 'contained' : 'outlined'}
      disabled={!trade.tradingViewUrls?.extra}
      onClick={() => setPreviewTimeframe('extra')}
    >
      Extra/Focus
    </Button>
  </MDBox>

  {(
    trade.tradingViewUrls?.[previewTimeframe] ||
    trade.tradingViewUrls?.htf ||
    trade.tradingViewUrls?.ltf ||
      trade.tradingViewUrls?.extra ||
    trade.screenshot
  ) ? (
    <img
      src={
        trade.tradingViewUrls?.[previewTimeframe] ||
        trade.tradingViewUrls?.htf ||
        trade.tradingViewUrls?.ltf ||
        trade.tradingViewUrls?.extra ||
        trade.screenshot
      }
      alt="Trade Chart"
      style={{ width: '100%', borderRadius: 8 }}
    />
  ) : (
    <MDTypography variant="body2">
      No screenshot or TradingView Chart Available
    </MDTypography>
  )}
</MDBox>

                  </MDBox>
                </MDBox>
              </AccordionDetails>
            </Accordion>
          ))
        ) : (
          <MDTypography variant="body2" color="textSecondary">
            No trades for this date.
          </MDTypography>
        )}
      </Dialog>
<Dialog
  visible={isAddTradeOpen}
  header={null}
  style={{ width: '60vw' }}
onHide={() => {
  setIsAddTradeOpen(false);     // close Add Trade dialog
  setAddTradeReady(false);     // 🔥 unmount TradeEntryForm
  setDisplayModal(false);      // close calendar modal
  allowTradeFormCloseRef.current = false;
}}
>
  <MDBox
    sx={{
      backgroundColor: theme.palette.background.default,
      borderRadius: 2,
      boxShadow: theme.shadows[3],
      overflow: 'hidden', // 👈 important for header radius
    }}
  >
    {/* HEADER */}
    <MDBox
      px={2}
      py={1.5}
      sx={{
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.default,
      }}
    >
  <MDTypography variant="h5">
    Add Trade — {selectedDate}
  </MDTypography>
</MDBox>
    

    {/* BODY */}
    <MDBox p={2} minHeight="200px">
      {addTradeReady && (
        <TradeEntryForm
          userID={userID}
          tradeAccountID={tradeAccountID}
          defaultDate={selectedDate}
onTradeSubmit={() => {
  setIsAddTradeOpen(false);     // close Add Trade dialog
  setDisplayModal(false);      // close calendar dialog
  setAddTradeReady(false);     // 🔥 UNMOUNT TradeEntryForm
  allowTradeFormCloseRef.current = false;
  fetchTrades();
}}
          setIsFormOpen={(open) => {
            if (!allowTradeFormCloseRef.current && open === false) {
              allowTradeFormCloseRef.current = true;
              return;
            }
            setIsAddTradeOpen(open);
          }}
        />
      )}
    </MDBox>
  </MDBox>
</Dialog>

<EditSingleTrade
  open={editTradeOpen}
  trade={editTradeData}
  userID={userID}
  tradeAccountID={tradeAccountID}
  onClose={() => {
    setEditTradeOpen(false);
    setEditTradeData(null);
  }}
  onUpdated={() => fetchTrades()} // refresh calendar after edit
/>

    </Card>
  );
};

MonthlyCalendar.propTypes = {
  userID: PropTypes.string.isRequired,
  tradeAccountID: PropTypes.string,
};

export default MonthlyCalendar;